import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toastError, toastSuccess } from "../../components/Toast";
import AuthService from "../../services/auth";
import Logo from "../../assets/images/logo/linkologo.png";
import PrivateIcon from "../../assets/images/icon/private.png";
import NoDataIcon from "../../assets/images/icon/nodata.png";
import ModalIcon from "../../assets/images/icon/modal.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useLanguage } from "../../context/LanguageContext";

const translations = {
  en: {
    title: "Reset Password",
    subtitle: "Enter your new password below.",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    passwordReq: "Password is required",
    passwordMin: "Password must be at least 8 characters",
    confirmReq: "Please confirm your password",
    passwordMatch: "Passwords must match",
    resetBtn: "Reset Password",
    successHeading: "Password reset!",
    successSub: "Your password has been updated successfully.",
    goToLogin: "Go to Log in",
    backToLogin: "← Back to log in",
    invalidHeading: "Link expired",
    invalidSub: "This reset link is invalid or has expired. Please request a new one.",
    requestNew: "Request new link",
    card1Title: "Private & Secure",
    card1Sub: "End-to-end encrypted",
    card2Title: "No Data Stored",
    card2Sub: "Zero  policy",
    card3Title: "No Training",
    card3Sub: " Data stays yours",
    leftTitle1: "Secure.",
    leftTitle2: "Private. Yours.",
    leftSubtitle: "Reset your password and get back to translating.",
  },
  es: {
    title: "Restablecer contraseña",
    subtitle: "Ingresa tu nueva contraseña a continuación.",
    newPassword: "Nueva contraseña",
    confirmPassword: "Confirmar contraseña",
    passwordReq: "La contraseña es obligatoria",
    passwordMin: "La contraseña debe tener al menos 8 caracteres",
    confirmReq: "Por favor confirma tu contraseña",
    passwordMatch: "Las contraseñas no coinciden",
    resetBtn: "Restablecer contraseña",
    successHeading: "¡Contraseña restablecida!",
    successSub: "Tu contraseña ha sido actualizada con éxito.",
    goToLogin: "Ir a iniciar sesión",
    backToLogin: "← Volver al inicio de sesión",
    invalidHeading: "Enlace expirado",
    invalidSub: "Este enlace es inválido o ha expirado. Por favor solicita uno nuevo.",
    requestNew: "Solicitar nuevo enlace",
    card1Title: "Privado y Seguro",
    card1Sub: "Cifrado de extremo a extremo",
    card2Title: "Sin almacenamiento",
    card2Sub: "Política de cero retención",
    card3Title: "Sin entrenamiento",
    card3Sub: "Tus datos son tuyos",
    leftTitle1: "Seguro.",
    leftTitle2: "Privado. Tuyo.",
    leftSubtitle: "Restablece tu contraseña y vuelve a traducir.",
  },
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { language, switchLanguage } = useLanguage();
  const t = translations[language] || translations.en;

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [tokenStatus, setTokenStatus] = useState("verifying"); // "verifying" | "valid" | "invalid"
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenStatus("invalid");
      return;
    }
    AuthService.verifyResetToken(token)
      .then((res) => {
        if (res?.data?.isVerify) {
          setTokenStatus("valid");
        } else {
          setTokenStatus("invalid");
        }
      })
      .catch(() => setTokenStatus("invalid"));
  }, [token]);

  const formik = useFormik({
    initialValues: { newPassword: "", confirmPassword: "" },
    validationSchema: Yup.object({
      newPassword: Yup.string().min(8, t.passwordMin).required(t.passwordReq),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], t.passwordMatch)
        .required(t.confirmReq),
    }),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await AuthService.resetPassword(token, values.newPassword, values.confirmPassword);
        setDone(true);
        toastSuccess(t.successHeading);
      } catch (error) {
        toastError(error?.response?.data?.message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const cards = [
    { icon: PrivateIcon, alt: "Private", title: t.card1Title, sub: t.card1Sub },
    { icon: NoDataIcon, alt: "No Data", title: t.card2Title, sub: t.card2Sub },
    { icon: ModalIcon, alt: "No Model", title: t.card3Title, sub: t.card3Sub },
  ];

  const renderRightContent = () => {
    if (tokenStatus === "verifying") {
      return (
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="w-10 h-10 border-[3px] border-white/20 border-t-white rounded-full animate-spin" />
          <p className="text-[#D2C5E8] text-[14px]">Verifying link…</p>
        </div>
      );
    }

    if (tokenStatus === "invalid") {
      return (
        <>
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="18" fill="#ef4444" fillOpacity="0.3" />
                <path d="M12 12l12 12M24 12L12 24" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <h2 className="text-[28px] lg:text-[32px] font-extrabold text-white">{t.invalidHeading}</h2>
            <p className="text-[#D2C5E8] text-[14px]">{t.invalidSub}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="w-full h-[53px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] transition-colors text-[16px]"
          >
            {t.requestNew}
          </button>
        </>
      );
    }

    if (done) {
      return (
        <>
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-[#904DB0]/30 flex items-center justify-center">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="18" fill="#904DB0" fillOpacity="0.3" />
                <path d="M10 18l6 6 10-12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="text-[28px] lg:text-[32px] font-extrabold text-white">{t.successHeading}</h2>
            <p className="text-[#D2C5E8] text-[14px]">{t.successSub}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="w-full h-[53px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] transition-colors text-[16px]"
          >
            {t.goToLogin}
          </button>
        </>
      );
    }

    return (
      <>
        <div>
          <h2 className="text-[22px] sm:text-[24px] lg:text-[23px] xl:text-[25px] 2xl:text-[30px] font-extrabold text-white mb-1.5 text-left w-full">{t.title}</h2>
          <p className="text-[#D2C5E8] text-[12.5px] sm:text-[13.5px] leading-relaxed text-left w-full">{t.subtitle}</p>
        </div>
        <form onSubmit={formik.handleSubmit} className="flex flex-col mt-3 sm:mt-4 2xl:mt-4.5">
          <div className="flex flex-col">
            <div className="flex flex-col">
              <div className="relative">
                <input
                  type={passwordVisible ? "text" : "password"}
                  name="newPassword"
                  placeholder={t.newPassword}
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full h-[38px] lg:h-[36px] xl:h-[38px] 2xl:h-[44px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-500 px-4 pr-10 text-[13px] lg:text-[13px] xl:text-[13.5px] 2xl:text-[14.5px] auth-right-input"
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-base"
                >
                  {passwordVisible ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
              <div className="min-h-[18px] sm:min-h-[20px] mt-0.5">
                {formik.touched.newPassword && formik.errors.newPassword && (
                  <div className="text-red-400 text-[11px] leading-tight">{formik.errors.newPassword}</div>
                )}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="relative">
                <input
                  type={confirmVisible ? "text" : "password"}
                  name="confirmPassword"
                  placeholder={t.confirmPassword}
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full h-[38px] lg:h-[36px] xl:h-[38px] 2xl:h-[44px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-500 px-4 pr-10 text-[13px] lg:text-[13px] xl:text-[13.5px] 2xl:text-[14.5px] auth-right-input"
                />
                <button
                  type="button"
                  onClick={() => setConfirmVisible((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-base"
                >
                  {confirmVisible ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
              <div className="min-h-[18px] sm:min-h-[20px] mt-0.5">
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <div className="text-red-400 text-[11px] leading-tight">{formik.errors.confirmPassword}</div>
                )}
              </div>
            </div>
          </div>
          <button
            type="submit"
            disabled={formik.isSubmitting}
            className="w-full h-[38px] lg:h-[38px] xl:h-[40px] 2xl:h-[46px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] shadow-login-btn transition-colors flex justify-center items-center gap-[10.94px] text-[13.5px] lg:text-[14px] xl:text-[14.5px] 2xl:text-[15.5px] mt-1.5 sm:mt-2 2xl:mt-3 auth-right-btn disabled:opacity-60"
          >
            {formik.isSubmitting
              ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : t.resetBtn}
          </button>
        </form>
        <div className="text-center text-[12px] xl:text-[14px] mt-2.5 sm:mt-3">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="bg-transparent border-none cursor-pointer text-white hover:text-[#D2C5E8] font-inherit hover:underline underline-offset-[4px] decoration-[#D2C5E8]/60"
          >
            {t.backToLogin}
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen lg:h-screen w-full font-roboto relative lg:overflow-hidden">

      {/* Mobile top bar — language dropdown */}
      <div className="lg:hidden absolute top-0 left-0 right-0 z-20 flex justify-end pt-3 px-4">
        <select
          value={language}
          onChange={(e) => switchLanguage(e.target.value)}
          className="bg-[#32235B] text-white border border-white/20 rounded-md py-1.5 px-3 focus:outline-none cursor-pointer shadow-lg hover:bg-[#433075] transition-colors text-sm font-medium"
        >
          <option value="es">ESP</option>
          <option value="en">ENG</option>
        </select>
      </div>

      {/* RIGHT SIDE — dark gradient with form */}
      <div className="w-full lg:w-1/2 lg:order-2 flex items-center justify-center pt-[80px] pb-10 px-4 sm:px-6 lg:pt-0 lg:pb-0 relative bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] min-h-screen lg:h-full lg:overflow-y-auto">

        {/* Language dropdown — desktop only */}
        <div className="hidden lg:block absolute top-8 right-8 z-20">
          <select
            value={language}
            onChange={(e) => switchLanguage(e.target.value)}
            className="bg-[#32235B] text-white border border-white/20 rounded-md py-1.5 px-3 focus:outline-none cursor-pointer shadow-lg hover:bg-[#433075] transition-colors text-sm font-medium"
          >
            <option value="es">ESP</option>
            <option value="en">ENG</option>
          </select>
        </div>

        <div className="w-full max-w-[435px] sm:max-w-[455px] xl:max-w-[465px] 2xl:max-w-[500px] relative z-10 bg-[rgba(199,191,229,0.10)] rounded-[22px] sm:rounded-[27px] border border-[rgba(199,191,229,0.10)] px-6 sm:px-8 py-6 sm:py-7 lg:px-8 lg:py-7 xl:px-8 xl:py-7 2xl:px-10 2xl:py-9 flex flex-col auth-right-card">
          {renderRightContent()}
        </div>
      </div>

      {/* LEFT SIDE — light bg with logo and content */}
      <div className="w-full lg:w-1/2 lg:order-1 flex flex-col relative bg-[#F7F8FD] px-4 sm:px-6 py-8 justify-center items-center lg:h-full overflow-hidden">
        <div className="absolute top-[18px] left-[14px]">
          <img src={Logo} alt="Logo" className="w-[140px] h-auto lg:w-[210px] lg:h-[87px] object-contain" />
        </div>

        <div className="w-full flex flex-col items-center max-w-[650px] mx-auto z-10 pt-16 sm:pt-24 lg:pt-32 pb-6 sm:pb-8">
          <h1 className="text-center text-[#1A0E3E] leading-[1.1] font-light w-full text-[28px] sm:text-[36px] lg:text-[48px]">
            {t.leftTitle1}<br /><span className="font-bold">{t.leftTitle2}</span>
          </h1>
          <p className="text-center text-[#3D2B90] mt-4 sm:mt-6 lg:mt-8 text-[17px] sm:text-[20px] lg:text-[24px] leading-snug font-light w-full max-w-[448px]">
            {t.leftSubtitle}
          </p>
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 lg:gap-3 mt-8 sm:mt-10 lg:mt-16 w-full max-w-[640px]">
            {cards.map(({ icon, alt, title, sub }) => (
              <div key={alt} className="flex items-center justify-center gap-1.5 sm:gap-2 bg-[#FFFFFF] rounded-[18px] sm:rounded-[26px] shadow-[0px_0px_23.6px_-11px_#C7BFE4] flex-1 h-[95px] sm:h-[110px] lg:h-[122.35px] px-2 lg:px-3 min-w-0">
                <img src={icon} alt={alt} className="h-[24px] sm:h-[30px] lg:h-[36px] w-auto object-contain shrink-0" />
                <div className="flex flex-col text-left min-w-0">
                  <span className="text-[#1A0E3E] font-bold text-[9px] sm:text-[10px] lg:text-[11px] leading-tight whitespace-nowrap">{title}</span>
                  <span className="text-[#1A0E3E] font-light text-[8px] sm:text-[9px] lg:text-[10px] leading-tight mt-0.5 whitespace-nowrap">{sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
